import { usePromoCodeForm, usePromoCodeFormMeta } from './formMeta';
import {
    PromoCodeContentButtonStyled,
    PromoCodeContentStyled,
    PromoCodeContentWrapperStyled,
    PromoCodeStyled,
} from './PromoCode.style';
import Checkbox from 'components/Forms/Checkbox/Checkbox';
import Form from 'components/Forms/Form/Form';
import ChoiceFormLine from 'components/Forms/Lib/ChoiceFormLine/ChoiceFormLine';
import TextInput from 'components/Forms/TextInput/TextInput';
import { isDomainLocale } from 'helpers/isDomainLocale';
import { useApplyPromoCodeToCart } from 'hooks/cart/useApplyPromoCodeToCart';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { SpinnerIcon } from 'public/svg/IconsSvg';
import { useRef, useState } from 'react';
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form';
import { CSSTransition } from 'react-transition-group';
import { twJoin } from 'tailwind-merge';
import { PromoCodeFormType } from 'types/form';

const TEST_IDENTIFIER = 'blocks-promocode';

const PromoCode: FC = () => {
    const t = useTypedTranslationFunction();
    const domainConfig = useDomainConfig();
    const [isContentVisible, setIsContentVisible] = useState(isDomainLocale(domainConfig.defaultLocale, 'pt'));
    const [contentElementHeight, setContentElementHeight] = useState(0);
    const contentElement = useRef<HTMLDivElement>(null);
    const cssTransitionRef = useRef<HTMLDivElement>(null);
    const [formProviderMethods] = usePromoCodeForm();
    const formMeta = usePromoCodeFormMeta(formProviderMethods);
    const [applyPromoCode, fetching, isValid] = useApplyPromoCodeToCart();

    const calcHeight = () => {
        if (contentElement.current) {
            setContentElementHeight(contentElement.current.clientHeight);
        }
    };

    const onApplyPromoCodeHandler: SubmitHandler<PromoCodeFormType> = async (data, event) => {
        event?.preventDefault();
        applyPromoCode(data.promoCode, formMeta.messages.addPromoCode);
    };

    return (
        <PromoCodeStyled contentElementHeight={contentElementHeight} data-testid={TEST_IDENTIFIER}>
            <ChoiceFormLine>
                <Checkbox
                    data-testid={TEST_IDENTIFIER + '-add-button'}
                    checked={isContentVisible}
                    label={t('I have a discount coupon or gift voucher')}
                    name={TEST_IDENTIFIER}
                    onChange={() => setIsContentVisible((prev) => !prev)}
                    value={isContentVisible}
                />
            </ChoiceFormLine>

            <CSSTransition
                in={isContentVisible}
                timeout={300}
                classNames="promoCode"
                onEnter={calcHeight}
                onExit={calcHeight}
                unmountOnExit
                nodeRef={cssTransitionRef}
            >
                <PromoCodeContentWrapperStyled ref={cssTransitionRef}>
                    <PromoCodeContentStyled ref={contentElement}>
                        <FormProvider {...formProviderMethods}>
                            <Form
                                onSubmit={formProviderMethods.handleSubmit(onApplyPromoCodeHandler)}
                                noValidate
                                style={{ display: 'flex' }}
                            >
                                <Controller
                                    name={formMeta.fields.promoCode.name}
                                    control={formProviderMethods.control}
                                    render={({ field }) => (
                                        <>
                                            <TextInput
                                                type="text"
                                                id={formMeta.formName + '-' + formMeta.fields.promoCode.name}
                                                label={formMeta.fields.promoCode.label}
                                                fieldRef={field}
                                                style={{ width: '100%', marginBottom: '0' }}
                                                className={twJoin(
                                                    !isValid && field.value !== '' && 'border-red shadow-redShadow',
                                                )}
                                            />
                                            <PromoCodeContentButtonStyled
                                                type="submit"
                                                data-testid={TEST_IDENTIFIER + '-apply-button'}
                                                size="small"
                                                isDisabled={fetching}
                                            >
                                                {fetching === true && <SpinnerIcon className="h-4 w-4" />}
                                                {t('Apply code')}
                                            </PromoCodeContentButtonStyled>
                                        </>
                                    )}
                                />
                            </Form>
                        </FormProvider>
                    </PromoCodeContentStyled>
                </PromoCodeContentWrapperStyled>
            </CSSTransition>
        </PromoCodeStyled>
    );
};

export default PromoCode;
